import React, { useState, useEffect } from 'react';
import { Control } from 'react-redux-form';
import './styles/TimePicker.scss';
import PropTypes from 'prop-types';

/**
 * Parses a time string in hours and minutes, like '16:20', converts to 12 hour format.
 *
 * ie parseTimeString('16:20') => {hours: 4. minutes: 20, amPm: 'PM'}.
 *
 * Returns midnight if any errors happen.
 *
 * Mods out of range numbers to best equivalent, i.e. "12:65" => "12:05"
 *
 * @param value {string} hours:minutes
 * @returns {{hours: number, amPm: string, minutes: number}|{hours: number, amPm: (string), minutes: number}}
 */
const parseTimeString = (value) => {
  try {
    const [
      realHours = '0',
      realMinutes = '0'
    ] = value.split(':');

    const realHoursInt = parseInt(realHours, 10) || 0;
    const realMinutesInt = parseInt(realMinutes, 10) || 0;

    const hours = realHoursInt % 12 || 12;
    const minutes = realMinutesInt % 60;
    const amPm = realHoursInt >= 12 ? 'PM' : 'AM';

    return {
      hours,
      minutes,
      amPm
    };
  } catch (e) {
    return {
      hours: 12,
      minutes: 0,
      amPm: 'AM'
    };
  }
};

/**
 * A time picker component that allows freeform entry of hours and minutes.
 *
 * @param value Current value in 24 hour format, i.e. '16:20'
 * @param onChange Callback to change value.  Takes new value as parameter
 * @returns {JSX.Element}
 * @constructor
 */
const TimePickerInternal = ({
  value,
  onChange
}) => {
  const {
    hours: parsedHours,
    minutes: parsedMinutes,
    amPm: parsedAmPm
  } = parseTimeString(value);

  const [hours, setHours] = useState(parsedHours);
  const [minutes, setMinutes] = useState(`${parsedMinutes}`.padStart(2, '0'));
  const [amPm, setAmPm] = useState(parsedAmPm);

  // reset state when value changes
  useEffect(() => {
    setHours(parsedHours);
    setMinutes(`${parsedMinutes}`.padStart(2, '0'));
    setAmPm(parsedAmPm);
  }, [value]);

  const onBlur = () => {
    const fixedHours = parseInt(hours, 10) % 12 || 12;
    const fixedMinutes = parseInt(minutes, 10) % 60 || 0;

    const paddedMinutes = `${fixedMinutes}`.padStart(2, '0');

    setHours(fixedHours);
    setMinutes(paddedMinutes);

    const amPmOffset = (amPm === 'PM') ? 12 : 0;
    const newValue = [
      `${(fixedHours % 12) + amPmOffset}`.padStart(2, '0'),
      paddedMinutes
    ].join(':');

    onChange(newValue);
  };

  return (
    <div className='time-picker'>
      <input
        type='text'
        value={hours}
        onChange={(event) => setHours(event.target.value)}
        onBlur={onBlur}
        className='timepicker-hours form-control form-control-sm'
        name='hour12'
        autoComplete='off'
      />

      <span className='separator'>:</span>

      <input
        type='text'
        value={minutes}
        onChange={(event) => setMinutes(event.target.value)}
        onBlur={onBlur}
        className='timepicker-minutes form-control form-control-sm'
        name='minute'
        autoComplete='off'
      />
      <select
        value={amPm}
        onChange={(event) => setAmPm(event.target.value)}
        onBlur={onBlur}
        className='timepicker-ampm form-select form-select-sm'
        name='amPm'
        autoComplete='off'
      >
        <option value='AM'>AM</option>
        <option value='PM'>PM</option>
      </select>
    </div>
  );
};

TimePickerInternal.propTypes = {
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

TimePickerInternal.defaultProps = {
  value: '00:00'
};

/**
 * A time input control for react-redux-forms.  Expects value of model property to be minutes and hours in 24 hour
 * format, i.e. '16:20'
 *
 * @param model The name of the model property being updated.
 * @param modelValue Current value of the model
 * @param defaultValue Default value if no current value
 * @param props Any other props to pass along to Control.text
 * @returns {JSX.Element}
 * @constructor
 */
const TimePicker = ({model, modelValue, ...props}) => {
  // we have two different timepickers currently in use -- see TimeSelector.jsx for the other one
  // This one allows for typing in numbers and passes a string to the controller

  return <Control
    {...props}
    model={model}
    component={TimePickerInternal}
    controlProps={{
      value: modelValue
    }}
  />;
};

TimePicker.propTypes = {
  model: PropTypes.string.isRequired,
  modelValue: PropTypes.string,
  defaultValue: PropTypes.string
};

TimePicker.defaultProps = {
  modelValue: undefined,
  defaultValue: '00:00'
};

export default TimePicker;
export const PlainTimePicker = TimePickerInternal;
